import React from 'react';
import styles from './PrivacyPolicy.module.css';

const PrivacyPolicy: React.FC = () => {
    return (
        <div className={styles.container}>
            <h1 className={styles.header}>
                TURTLTRACKER LLP Privacy Policy
            </h1>
            <p className={styles.version}>
                Version 1.0
            </p>
            <p className={styles.effectiveDate}>
                Effective Date: October 13, 2025
            </p>
            <p className={styles.description}>
                This Privacy Policy explains how TURTLTRACKER LLP ("TurtlTracker", "we", "us", "our") collects, uses, discloses, and protects information when you use our mobile application and related services (collectively, the "Services").
            </p>
            
            <h2 className={styles.subheader}>1. Scope and Roles</h2>
            <ul className={styles.list}>
                <li><strong>Scope:</strong> This policy applies to the Services wherever they are offered.</li>
                <li><strong>Controller/Business:</strong> For the EU/UK, we act as a "controller." For U.S. state privacy laws (e.g., California), we act as a "business."</li>
                <li><strong>Contact:</strong> See Section 14 for how to contact us about privacy.</li>
            </ul>
            
            <h2 className={styles.subheader}>2. Categories of Personal Information We Collect</h2>
            <ul className={styles.list}>
                <li><strong>Account Information:</strong> username, email, display name, state/region.</li>
                <li><strong>Content You Submit:</strong> photos, comments, sighting details (species, time, place you choose to record).</li>
                <li><strong>Location Information:</strong>
                    <ul>
                        <li>Device location (only with your permission and only when features require it, e.g., attaching a location to a sighting or photo).</li>
                        <li>Photo metadata (e.g., GPS coordinates embedded in images you upload).</li>
                        <li>We do not collect continuous background location unless you explicitly enable a feature that clearly requires it.</li>
                    </ul>
                </li>
                <li><strong>Usage/Device Information:</strong> app interactions, device/OS info, diagnostics (e.g., crash logs, performance data).</li>
                <li><strong>Authentication/Session Data:</strong> secure tokens stored via platform secure storage.</li>
                <li><strong>Communications:</strong> messages you send us (e.g., support emails, abuse reports).</li>
            </ul>
            <p className={styles.description}>
                <strong>CPRA Category Mapping (California):</strong> identifiers (e.g., email), internet/activity data (usage), geolocation (when you choose to attach it), user-generated content, inferences (limited; e.g., content preferences), and account profile data. We do not knowingly collect "sensitive personal information" as defined by CPRA except precise geolocation when you choose to attach it to content (see Section 4).
            </p>
            
            <h2 className={styles.subheader}>3. Sources of Personal Information</h2>
            <ul className={styles.list}>
                <li>Directly from you (account setup, content uploads, in-app actions).</li>
                <li>Automatically via the app and your device (e.g., diagnostics, limited analytics).</li>
                <li>From photos you upload (embedded metadata such as GPS coordinates).</li>
                <li>From service providers that support the Services (e.g., crash reporting).</li>
            </ul>
            
            <h2 className={styles.subheader}>4. Precise Location and Sensitive Personal Information</h2>
            <ul className={styles.list}>
                <li><strong>Precise Location:</strong> We request device location permissions only when required by a feature (e.g., attaching a location to a sighting). You can decline or revoke permission in your device settings at any time.</li>
                <li><strong>Photo Metadata:</strong> If you upload photos with embedded GPS data, that information may be processed and displayed with your content according to your actions/settings.</li>
                <li><strong>Sensitive Data:</strong> Other than precise location data you choose to provide, we do not seek to collect sensitive personal information (e.g., health, financial, government identifiers). Do not include sensitive personal information in content you submit.</li>
                <li><strong>CPRA Limitation:</strong> If applicable to you, you may request we limit the use/disclosure of sensitive personal information (see Section 9).</li>
            </ul>
            
            <h2 className={styles.subheader}>5. How We Use Personal Information and Legal Bases (GDPR/UK GDPR)</h2>
            <p className={styles.description}>
                We use your information to:
            </p>
            <ul className={styles.list}>
                <li><strong>Provide the Services:</strong> operate core features (profiles, sightings, maps), process your content, facilitate community features.
                    <ul>
                        <li>Legal bases: performance of a contract (Art. 6(1)(b)); our legitimate interests to operate our Services (Art. 6(1)(f)).</li>
                    </ul>
                </li>
                <li><strong>Personalize and improve:</strong> tailor content (e.g., show relevant sightings near a selected location), improve app performance and user experience.
                    <ul>
                        <li>Legal bases: legitimate interests (improvement, personalization); consent when required.</li>
                    </ul>
                </li>
                <li><strong>Safety and integrity:</strong> detect, prevent, and respond to abuse, fraud, violations of terms, or harmful conduct; moderate user-generated content.
                    <ul>
                        <li>Legal bases: legitimate interests (safety/integrity); legal obligation when applicable.</li>
                    </ul>
                </li>
                <li><strong>Communications:</strong> respond to your requests, send service notices (e.g., changes to terms).
                    <ul>
                        <li>Legal bases: performance of a contract; legitimate interests (customer support).</li>
                    </ul>
                </li>
                <li><strong>Compliance and legal:</strong> comply with applicable laws, regulations, legal processes.
                    <ul>
                        <li>Legal bases: legal obligation; legitimate interests (establish/exercise/defend legal claims).</li>
                    </ul>
                </li>
                <li><strong>With your consent:</strong> where required (e.g., optional features that use precise location), we rely on your consent. You can withdraw consent at any time in your device or in-app settings where available.</li>
            </ul>
            
            <h2 className={styles.subheader}>6. Sharing and Disclosures</h2>
            <p className={styles.description}>
                We do not sell personal information and we do not "share" it for cross-context behavioral advertising as defined by CPRA.
            </p>
            <p className={styles.description}>
                We disclose personal information to:
            </p>
            <ul className={styles.list}>
                <li><strong>Service Providers/Processors:</strong> cloud hosting, storage, content delivery, analytics/diagnostics, email/notification services. Our infrastructure leverages Microsoft Azure (e.g., Azure App Service, Azure SQL, Azure Blob/CDN). Providers are contractually bound to handle data only on our instructions and to maintain appropriate security.</li>
                <li><strong>Other Users (Community Content):</strong> content you choose to make visible (e.g., sightings, photos, associated location) may be visible to others consistent with the functionality you select.</li>
                <li><strong>Legal and Safety:</strong> to comply with law or legal processes, enforce our terms, or protect users and Services.</li>
                <li><strong>Business Transfers:</strong> in connection with a merger, acquisition, or asset sale, we may transfer relevant data subject to this Policy and applicable law.</li>
            </ul>
            <p className={styles.description}>
                <strong>Google Maps/Places:</strong> Where applicable, we proxy Google services through our backend (a minimal BFF proxy) so that our server-side key is not exposed in the app. Requests are transmitted over HTTPS.
            </p>
            
            <h2 className={styles.subheader}>7. International Data Transfers</h2>
            <p className={styles.description}>
                Your information may be processed and stored in the United States and other countries where we or our providers operate. Where required, we use appropriate safeguards for international transfers (e.g., Standard Contractual Clauses).
            </p>
            
            <h2 className={styles.subheader}>8. Retention</h2>
            <p className={styles.description}>
                We retain personal information only as long as necessary to provide the Services, for legitimate business purposes, and to meet legal obligations. Typical retention periods include:
            </p>
            <ul className={styles.list}>
                <li><strong>Account data:</strong> retained while your account is active; deleted or anonymized within 30 days after confirmed account deletion, subject to legal holds.</li>
                <li><strong>User content (e.g., sightings, photos, location attached to content):</strong> retained until you delete it or delete your account; may remain in backups for up to 35 days.</li>
                <li><strong>Authentication/session data:</strong> rotated frequently; short-lived tokens per platform security guidance.</li>
                <li><strong>Logs and diagnostics:</strong> typically up to 90 days for security/operational purposes; crash analytics up to 13 months where required by the provider for trend analysis.</li>
                <li><strong>Legal/compliance records:</strong> as required by applicable law.</li>
            </ul>
            <p className={styles.description}>
                We may anonymize/de-identify data for analytics and research; we may retain anonymized data indefinitely.
            </p>
            
            <h2 className={styles.subheader}>9. Your Rights and Choices</h2>
            <p className={styles.description}>
                <strong>Permissions and Controls</strong>
            </p>
            <ul className={styles.list}>
                <li><strong>Device permissions:</strong> control location, camera, photos, and notifications in your device settings.</li>
                <li><strong>Content controls:</strong> you may edit or delete your content; community moderation policies may apply.</li>
            </ul>
            <p className={styles.description}>
                <strong>EU/UK GDPR Rights (where applicable)</strong>
            </p>
            <ul className={styles.list}>
                <li>Access, rectification, erasure, restriction, portability, and objection.</li>
                <li>Withdraw consent where processing is based on consent.</li>
                <li>Lodge a complaint with a supervisory authority.</li>
            </ul>
            <p className={styles.description}>
                <strong>U.S. State Privacy Rights (e.g., California CPRA, Colorado, Connecticut, Virginia, Utah)</strong>
            </p>
            <ul className={styles.list}>
                <li>Right to know/access, correct, delete, obtain a portable copy, and non-discrimination.</li>
                <li>Right to opt out of "sale"/"sharing" of personal information and targeted advertising. We do not sell or share personal information under CPRA.</li>
                <li>Right to limit use/disclosure of sensitive personal information (CPRA). We only process precise location when you choose to attach it; you may disable location permissions or request removal of location from specific content.</li>
                <li>Right to appeal (where applicable): If we deny a request, you may appeal by replying to our response or emailing us with "Appeal" in the subject line.</li>
            </ul>
            <p className={styles.description}>
                <strong>How to Exercise Your Rights</strong>
            </p>
            <ul className={styles.list}>
                <li><strong>Email:</strong> inquiries@turtltracker.com.</li>
                <li><strong>Verification:</strong> we may request information to verify your identity (e.g., account email verification).</li>
                <li><strong>Authorized agents (California):</strong> you may designate an agent; we may require proof of authorization and your identity.</li>
            </ul>
            
            <h2 className={styles.subheader}>10. Security</h2>
            <p className={styles.description}>
                We implement industry-standard technical and organizational measures to protect data in transit and at rest, including encryption and access controls. No method of transmission or storage is 100% secure.
            </p>
            
            <h2 className={styles.subheader}>11. Children's Privacy</h2>
            <p className={styles.description}>
                Our Services are not directed to children under 13 (or the age of digital consent where you live). We do not knowingly collect personal information from children under 13. If you believe a child provided us personal information, contact us to request deletion.
            </p>
            
            <h2 className={styles.subheader}>12. Automated Decision-Making</h2>
            <p className={styles.description}>
                We do not use automated decision-making that produces legal or similarly significant effects without human involvement.
            </p>
            
            <h2 className={styles.subheader}>13. Changes to This Policy</h2>
            <p className={styles.description}>
                We may update this Policy from time to time. We will post the updated version and revise the "Effective Date" above. Where required, we will provide additional notice (e.g., in-app notice or email).
            </p>
            
            <h2 className={styles.subheader}>14. Contact Us</h2>
            <p className={styles.description}>
                TURTLTRACKER LLP<br/>
                Email: inquiries@turtltracker.com<br/>
                Website: https://turtltracker.com
            </p>
        </div>
    );
};

export default PrivacyPolicy;

